import type { JSX } from 'react';
import { Alert, Anchor, Button, Card, Group, List, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, offersApi } from '../api';
import { linkTo } from '../ui/links';
import { SAMPLE_OFFER_CODE, SAMPLE_PROJECT_NUMBER, createSample } from './sample';

/**
 * Einstellungen › Beispieldaten: the sample project and offer to click
 * through. Each is made again when it is missing - deleted by mistake, or
 * deleted on purpose to start over.
 */
export function SamplePanel(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const offers = useQuery({ queryKey: ['offers'], queryFn: offersApi.list });
  const project = projects.data?.find((p) => p.number === SAMPLE_PROJECT_NUMBER);
  const offer = offers.data?.find((o) => o.code === SAMPLE_OFFER_CODE);
  const make = useMutation({
    mutationFn: createSample,
    onSettled: () => queryClient.invalidateQueries(),
  });
  const message = make.error instanceof ApiError || make.error instanceof Error ? make.error.message : 'Anlegen fehlgeschlagen.';
  const row = (label: string, what: string, exists: { href: string; name: string } | undefined, kind: 'project' | 'offer') => (
    <Card withBorder padding="sm">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Text fw={600}>{label}</Text>
          <Text size="sm" c="dimmed">{what}</Text>
          {exists
            ? <Text size="sm">Vorhanden: <Anchor {...linkTo(navigate, exists.href)}>{exists.name}</Anchor></Text>
            : <Text size="sm" c="dimmed">Nicht vorhanden.</Text>}
        </Stack>
        <Button variant="light" style={{ flexShrink: 0 }} disabled={!!exists || projects.isPending || offers.isPending} loading={make.isPending && make.variables?.[kind]}
          onClick={() => make.mutate({ project: kind === 'project', offer: kind === 'offer' })}>
          {exists ? 'angelegt' : 'Neu anlegen'}
        </Button>
      </Group>
    </Card>
  );
  return (
    <Stack gap="sm" maw={820}>
      <Text size="sm">
        Zum Ausprobieren: ein Beispielprojekt und ein Beispielangebot mit erfundenen Inhalten. Beide lassen sich wie jedes andere
        Projekt und Angebot bearbeiten und löschen; fehlt eines, legt PST es hier neu an. Wer neu beginnen will, löscht das Beispiel und legt es neu an.
      </Text>
      {row(`Beispielprojekt ${SAMPLE_PROJECT_NUMBER}`, 'Kita Sonnenwinkel: Projektdaten und Kontakte, Terminplan mit Basisplan und vereinfachter Fassung, Kostenplanung, Zeiterfassung, Vergabeeinheit mit Bietern und Zuschlag – je nachdem, welche Module aktiv sind.',
        project && { href: `/projekte/${project.id}`, name: `${project.number} ${project.name}` }, 'project')}
      {row(`Beispielangebot ${SAMPLE_OFFER_CODE}`, 'Freiraumkonzept Schulhof: Kalkulation mit Rollen, Nachunternehmer, Positionen und Zusatzleistungen, Texte, Rückfragen, Risiken, HOAI-Berechnung, Beteiligte und Referenz.',
        offer && { href: `/angebote/${offer.id}`, name: `${offer.code} ${offer.name}` }, 'offer')}
      {make.isError && <Alert color="red" py={6}>{message}</Alert>}
      {make.data && (
        <Alert color="brand" py={6} title="Angelegt">
          <List size="sm">{make.data.done.map((d) => <List.Item key={d}>{d}</List.Item>)}</List>
        </Alert>
      )}
      <Text size="xs" c="dimmed">
        Angelegt wird mit den eigenen Rechten; das Projekt erhält Sie als Mitglied. Ist die Projektnummer {SAMPLE_PROJECT_NUMBER} schon an ein Projekt vergeben,
        das Sie nicht sehen, meldet PST das. Personen, Firmen und Orte im Beispiel sind erfunden.
      </Text>
    </Stack>
  );
}
